import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Mic, Ear, Upload, Sparkles, ArrowLeft, AlertTriangle, Trash2, Play, Pause, Square, Check, X, Loader2, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useShakeDetection } from '../hooks/useShakeDetection';
import { useBluetoothButton } from '../hooks/useBluetoothButton';
import { useMediaButton } from '../hooks/useMediaButton';
import { useSetting, useVoiceTriggerSetting, useAudioUploadSetting, useExtendedErrorReportsSetting } from '../hooks/useSetting';
import { settings } from '../services/settingsService';
import { getCurrentInspection, clearSession, retryStaleSegment } from '../services/segmentService';
import { formatDuration } from '../services/audioProcessingService';
import { deleteInspection, deleteSegment, fetchUserSettings, updateUserSettings, fetchInspectionById } from '../services/inspectionService';
import { updateInspection } from '../services/uploadService';
import { getChunksForRecording, getChunkById, deleteChunkById, ChunkStatus } from '../services/chunkStorage';
import { getInputDevices, releaseStream, getMicrophoneStream } from '../services/microphoneService';
import { useVoskVoiceTrigger, shouldUseVosk } from '../hooks/useVoskVoiceTrigger';
import AppBarMenu from './AppBarMenu';
import Logo from './Logo';

// German license plate restricted letter combinations (historically sensitive)
const RESTRICTED_COMBINATIONS = ['HJ', 'KZ', 'NS', 'SA', 'SS', 'SD'];

/**
 * Generate inspection title based on vehicleId or auto-generate if empty
 * @param {string} vehicleId - Vehicle ID (optional)
 * @param {string} locale - Current locale ('de' or 'en')
 * @returns {string} Title for the inspection
 */
const generateInspectionTitle = (vehicleId, locale) => {
  if (vehicleId && vehicleId.trim()) {
    return vehicleId.trim();
  }

  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');

  // Format: "HU DD.MM.YYYY HH:MM" (German) or "GI DD.MM.YYYY HH:MM" (English)
  const prefix = locale === 'de' ? 'HU' : 'GI';
  return `${prefix} ${day}.${month}.${year} ${hours}:${minutes}`;
};

/**
 * Format German license plate input
 * - Auto-capitalize
 * - Remove non-alphanumeric characters (except spaces and dashes)
 * - Trim whitespace
 *
 * German format: [1-3 letters city code]-[1-2 letters] [1-4 numbers]
 * Examples: B-AB 1234, M-X 9999, HH-AB 123
 */
const formatGermanPlate = (input) => {
  // Remove all non-alphanumeric except spaces and dashes
  let cleaned = input.replace(/[^A-Za-z0-9\s-]/g, '').toUpperCase();

  // Remove multiple consecutive spaces/dashes
  cleaned = cleaned.replace(/[\s-]+/g, match => match[0]);

  // Trim leading/trailing whitespace
  cleaned = cleaned.trim();

  // Limit total length to 12 characters (reasonable max for German plates with formatting)
  return cleaned.slice(0, 12);
};

/**
 * Validate German license plate format
 * Returns: { valid: boolean, error?: string }
 *
 * German license plate format:
 * - 1-3 letters (city/district code)
 * - Separator (dash or space)
 * - 1-2 letters
 * - 1-4 numbers
 * - Total alphanumeric characters: 2-8
 */
const validateGermanPlate = (plate, t) => {
  if (!plate || plate.trim().length === 0) {
    return { valid: true }; // Empty is valid - vehicleId is optional
  }

  // Remove separators for validation
  const alphanumeric = plate.replace(/[\s-]/g, '');

  // Check minimum length (at least 2 alphanumeric chars)
  if (alphanumeric.length < 2) {
    return { valid: false, error: t('recording.vehicle_id_too_short') };
  }

  // Check maximum length (max 8 alphanumeric chars)
  if (alphanumeric.length > 8) {
    return { valid: false, error: t('recording.vehicle_id_too_long') };
  }

  // Check for restricted combinations anywhere in the plate
  for (const restricted of RESTRICTED_COMBINATIONS) {
    if (alphanumeric.includes(restricted)) {
      return { valid: false, error: t('recording.vehicle_id_restricted', { combination: restricted }) };
    }
  }

  // Strict German format validation (optional - warn but don't block)
  // Format: 1-3 letters, separator optional, 1-2 letters, 1-4 numbers
  const germanPlateRegex = /^[A-Z]{1,3}[\s-]?[A-Z]{1,2}[\s-]?[0-9]{1,4}$/;
  const isGermanFormat = germanPlateRegex.test(plate);

  if (!isGermanFormat && alphanumeric.length >= 3) {
    // Only show format hint if they've typed enough characters
    return { valid: true, warning: t('recording.vehicle_id_format_hint') };
  }

  return { valid: true };
};

/**
 * Check if a phrase matches in the transcript.
 * Handles multi-word phrases by checking if all words appear.
 */
const phraseMatches = (transcript, phrase) => {
  const normalizedTranscript = transcript.toLowerCase().trim();
  const normalizedPhrase = phrase.toLowerCase().trim();

  // Direct substring match
  if (normalizedTranscript.includes(normalizedPhrase)) {
    return true;
  }

  // For multi-word phrases, check if all words appear in the transcript
  const phraseWords = normalizedPhrase.split(/\s+/);
  if (phraseWords.length > 1) {
    return phraseWords.every(word => normalizedTranscript.includes(word));
  }

  return false;
};

// Alert component
const Alert = ({ severity, icon: Icon, children }) => {
  const styles = {
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className={`px-4 py-3 border rounded-lg flex items-start gap-3 ${styles[severity]}`}>
      {Icon && <Icon size={20} className="flex-shrink-0 mt-0.5 animate-pulse" />}
      <div>{children}</div>
    </div>
  );
};

// Dialog component
const Dialog = ({ open, onClose, title, children, actions }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface rounded-lg shadow-lg max-w-sm w-full p-6">
        <h2 className="text-lg font-semibold text-text mb-2">{title}</h2>
        <div className="text-text-muted mb-6">{children}</div>
        <div className="flex justify-end gap-3">
          {actions}
        </div>
      </div>
    </div>
  );
};

const VehicleInput = ({ onBack, onStartRecording, onFileUpload, onTranscribe, onCancel, onLogout, initialVehicleId, isAdmin = false, onDelete }) => {
  const { t, i18n } = useTranslation();

  // Initialize from prop, current session, or empty (placeholder will show)
  const [vehicleId, setVehicleId] = useState(() => {
    console.log('[VehicleInput] Initializing vehicleId. initialVehicleId:', initialVehicleId);
    if (initialVehicleId) return initialVehicleId;
    const inspection = getCurrentInspection();
    console.log('[VehicleInput] From localStorage:', inspection);
    return inspection?.vehicleId || '';
  });

  // Get stored title from current session (set when recording started)
  const [storedTitle, setStoredTitle] = useState(() => {
    const inspection = getCurrentInspection();
    return inspection?.title || null;
  });

  // Validate German license plate format
  const validation = useMemo(() => validateGermanPlate(vehicleId, t), [vehicleId, t]);
  const isVehicleIdValid = validation.valid;

  // Handle vehicle ID input change with formatting
  const handleVehicleIdChange = useCallback((e) => {
    const formatted = formatGermanPlate(e.target.value);
    setVehicleId(formatted);
  }, []);

  // Handle vehicle ID blur - update inspection if vehicleId changed
  const handleVehicleIdBlur = useCallback(async () => {
    const inspection = getCurrentInspection();
    if (!inspection?.inspectionId) return;

    const trimmedVehicleId = vehicleId?.trim() || '';
    const currentStoredVehicleId = inspection.vehicleId || '';

    // Only update if vehicleId actually changed
    if (trimmedVehicleId !== currentStoredVehicleId) {
      try {
        // If vehicleId is cleared, generate a default title
        const newTitle = trimmedVehicleId || generateInspectionTitle(null, i18n.language);

        console.log('[VehicleInput] Updating inspection on blur with vehicleId:', trimmedVehicleId || '(empty)', 'title:', newTitle);
        await updateInspection(inspection.inspectionId, {
          vehicleId: trimmedVehicleId || null,
          title: newTitle
        });
        // Update local session storage
        localStorage.setItem('currentInspection', JSON.stringify({
          ...inspection,
          vehicleId: trimmedVehicleId || '',
          title: newTitle
        }));
        setStoredTitle(newTitle);
      } catch (err) {
        console.error('[VehicleInput] Failed to update inspection on blur:', err);
      }
    }
  }, [vehicleId, i18n.language]);

  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [segments, setSegments] = useState([]);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [playingSegmentId, setPlayingSegmentId] = useState(null);
  const [deletingSegmentId, setDeletingSegmentId] = useState(null);
  const [inputDevices, setInputDevices] = useState([]);
  // INTERIM: Always use default device - ignore saved preference
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);
  // Track if we have pending chunks (for faster polling) - use ref to avoid effect re-runs
  const hasPendingChunksRef = useRef(false);
  // Check if a recording session exists with segments (vehicle ID should be read-only only when segments exist)
  const hasRecordedSegments = segments.length > 0;

  // DEBUG: Track mount/unmount to diagnose page reload issue
  useEffect(() => {
    const mountTime = Date.now();
    console.log('[VehicleInput] MOUNTED at', new Date().toISOString());
    return () => {
      console.log('[VehicleInput] UNMOUNTED after', (Date.now() - mountTime) / 1000, 'seconds');
    };
  }, []);

  // NOTE: We intentionally do NOT navigate from /record to /record/{id} here
  // because it causes RecordingWizard to remount (different routes), losing state.
  // The URL update will happen via RecordingWizard when appropriate (e.g., on manual page load).

  // Load segments list - from local IndexedDB or API (cross-device sync)
  const loadSegments = useCallback(async () => {
    const inspection = getCurrentInspection();
    console.log('[VehicleInput] loadSegments - inspection:', inspection);

    // Update stored title if available and changed (e.g., after returning from recording)
    // Use functional update to avoid unnecessary re-renders
    if (inspection?.title) {
      setStoredTitle(prev => prev !== inspection.title ? inspection.title : prev);
    }

    if (!inspection?.inspectionId) {
      setSegments([]);
      return;
    }

    // First, try local IndexedDB
    const localChunks = await getChunksForRecording(inspection.inspectionId);
    console.log('[VehicleInput] loadSegments - local chunks found:', localChunks.length);

    if (localChunks.length > 0) {
      // We have local chunks - sync with API to detect remote deletions AND add remote segments
      let apiSegments = [];
      let apiSegmentFilenames = new Set();
      try {
        const apiInspection = await fetchInspectionById(inspection.inspectionId);
        if (apiInspection?.segments) {
          apiSegments = apiInspection.segments;
          apiSegmentFilenames = new Set(apiSegments.map(s => s.filename));
        }
      } catch (err) {
        console.log('[VehicleInput] Could not fetch API segments for sync:', err.message);
      }

      // Filter out local chunks that were deleted remotely (if we got API data)
      let syncedChunks = localChunks;
      if (apiSegmentFilenames.size > 0 || localChunks.every(c => c.status === 'UPLOADED')) {
        // Only sync if API returned data OR all local chunks are uploaded
        const chunksToDelete = [];

        // Debug: Log what we're comparing
        console.log('[VehicleInput] Sync check - API filenames:', [...apiSegmentFilenames]);
        console.log('[VehicleInput] Sync check - Local filenames:', localChunks.map(c => c.filename));

        syncedChunks = localChunks.filter(chunk => {
          // Keep chunks that are still pending upload (not yet in S3)
          if (chunk.status !== 'UPLOADED') {
            return true;
          }

          // Don't filter chunks that were just uploaded - API might not have indexed them yet
          // This prevents flickering during the upload-to-API-indexing gap
          const uploadedAt = chunk.uploadedAt || chunk.updatedAt;
          if (uploadedAt && Date.now() - uploadedAt < 30000) {
            return true;
          }

          // For uploaded chunks, check if they exist in API response
          // Compare without extension since local might store without ext and API with ext
          const localFilenameBase = chunk.filename?.replace(/\.[^.]+$/, '') || '';
          const existsInApi = [...apiSegmentFilenames].some(apiFilename => {
            const apiFilenameBase = apiFilename?.replace(/\.[^.]+$/, '') || '';
            return localFilenameBase === apiFilenameBase || chunk.filename === apiFilename;
          });

          if (!existsInApi && apiSegmentFilenames.size > 0) {
            console.log('[VehicleInput] Local chunk deleted remotely, removing:', chunk.filename, '(not found in API)');
            chunksToDelete.push(chunk.id);
          }
          return existsInApi || apiSegmentFilenames.size === 0;
        });

        // Clean up deleted chunks from IndexedDB
        for (const chunkId of chunksToDelete) {
          try {
            await deleteChunkById(chunkId);
          } catch (err) {
            console.log('[VehicleInput] Failed to delete stale chunk:', err.message);
          }
        }
      }

      // Add remote segments that don't exist locally (cross-device sync)
      if (apiSegments.length > 0) {
        const localFilenames = new Set(syncedChunks.map(c => {
          const base = c.filename?.replace(/\.[^.]+$/, '') || '';
          return base;
        }));

        const remoteOnlySegments = apiSegments.filter(seg => {
          const apiBase = seg.filename?.replace(/\.[^.]+$/, '') || '';
          return !localFilenames.has(apiBase);
        }).map((seg) => ({
          id: `api-${seg.index}`,  // Synthetic ID for API-sourced segments
          recordingId: inspection.inspectionId,
          chunkIndex: seg.index,
          timestamp: seg.createdAt ? new Date(seg.createdAt).getTime() : (seg.uploadedAt ? new Date(seg.uploadedAt).getTime() : Date.now()),
          duration: seg.durationSec || 0,
          originalDuration: seg.originalDurationSec,
          silenceRemoved: seg.silenceRemovedSec,
          status: seg.status === 'UPLOADED' || seg.status === 'PROCESSED'
            ? ChunkStatus.UPLOADED
            : seg.status === 'FAILED'
              ? ChunkStatus.FAILED
              : ChunkStatus.PENDING,
          s3Key: seg.s3Key,
          filename: seg.filename,
          fileSizeBytes: seg.fileSizeBytes,
          audioUrl: seg.audioUrl,
          isRemote: true,  // Mark as remote-only (no local blob)
          error: seg.errorMessage,
          errorMessage: seg.errorMessage,
          errorTimestamp: seg.errorTimestamp
        }));

        if (remoteOnlySegments.length > 0) {
          console.log('[VehicleInput] Adding remote-only segments:', remoteOnlySegments.length);
          syncedChunks = [...syncedChunks, ...remoteOnlySegments];
        }
      }

      // Sort by timestamp to maintain chronological order
      syncedChunks.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

      // Update segments
      setSegments(syncedChunks);

      // Track if we have pending chunks (for faster polling)
      hasPendingChunksRef.current = syncedChunks.some(c =>
        c.status === ChunkStatus.PENDING || c.status === ChunkStatus.UPLOADING
      );
      return;
    }

    // If local is empty, try fetching from API (cross-device scenario)
    console.log('[VehicleInput] Local IndexedDB empty, fetching from API...');
    try {
      const apiInspection = await fetchInspectionById(inspection.inspectionId);
      if (apiInspection?.segments && apiInspection.segments.length > 0) {
        console.log('[VehicleInput] Got segments from API:', apiInspection.segments.length);

        // Map API segments to local format for display
        const apiSegments = apiInspection.segments.map((seg) => ({
          id: `api-${seg.index}`,  // Synthetic ID for API-sourced segments
          recordingId: inspection.inspectionId,
          chunkIndex: seg.index,
          // Use createdAt (when segment was recorded) not uploadedAt (when uploaded to S3)
          // createdAt is ISO string in local time, parse it to get timestamp
          timestamp: seg.createdAt ? new Date(seg.createdAt).getTime() : (seg.uploadedAt ? new Date(seg.uploadedAt).getTime() : Date.now()),
          duration: seg.durationSec || 0,
          originalDuration: seg.originalDurationSec,
          silenceRemoved: seg.silenceRemovedSec,
          status: seg.status === 'UPLOADED' || seg.status === 'PROCESSED'
            ? ChunkStatus.UPLOADED
            : ChunkStatus.PENDING,
          s3Key: seg.s3Key,
          filename: seg.filename,
          fileSizeBytes: seg.fileSizeBytes,
          audioUrl: seg.audioUrl,  // Presigned URL for remote playback
          isRemote: true
        }));

        setSegments(apiSegments);
        // API segments are always uploaded, no pending chunks
        hasPendingChunksRef.current = false;
      } else {
        setSegments([]);
      }
    } catch (err) {
      console.log('[VehicleInput] Failed to fetch segments from API:', err.message);
      setSegments([]);
    }
  }, []);

  // Track segments being auto-retried to prevent duplicate retries
  const retryingSegmentsRef = useRef(new Set());

  // Load segments on mount and periodically refresh
  // Use dynamic polling interval based on pending chunks (checked via ref to avoid effect re-runs)
  useEffect(() => {
    loadSegments();

    // Dynamic interval: poll faster when there are pending chunks
    const pollInterval = async () => {
      await loadSegments();

      // Auto-retry stale segments (stuck in PENDING/UPLOADING for > 60s)
      const inspection = getCurrentInspection();
      if (inspection?.inspectionId) {
        const localChunks = await getChunksForRecording(inspection.inspectionId);
        const now = Date.now();
        const staleThresholdMs = 60000; // 60 seconds

        for (const chunk of localChunks) {
          // Check if segment is stale (PENDING/UPLOADING for too long)
          const isStale = (chunk.status === ChunkStatus.PENDING || chunk.status === ChunkStatus.UPLOADING) &&
                          chunk.timestamp && (now - chunk.timestamp > staleThresholdMs);

          // Only retry if has blob and not already being retried
          if (isStale && chunk.blob && !retryingSegmentsRef.current.has(chunk.id)) {
            console.log('[VehicleInput] Auto-retrying stale segment:', chunk.id, 'age:', Math.floor((now - chunk.timestamp) / 1000), 's');
            retryingSegmentsRef.current.add(chunk.id);

            // Fire and forget - don't await, let it run in background
            retryStaleSegment(inspection.inspectionId, chunk.id, null)
              .finally(() => {
                retryingSegmentsRef.current.delete(chunk.id);
                // Trigger a refresh after retry completes
                loadSegments();
              });
          }
        }
      }
    };

    // Start with fast polling, will slow down once all chunks are uploaded
    // Using a self-adjusting interval pattern
    let timeoutId;
    const schedulePoll = () => {
      const interval = hasPendingChunksRef.current ? 500 : 3000;
      timeoutId = setTimeout(() => {
        pollInterval();
        schedulePoll();
      }, interval);
    };
    schedulePoll();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loadSegments]);

  // Cleanup audio playback and URL on unmount
  useEffect(() => {
    return () => {
      // Stop any playing audio when leaving the screen
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
    };
  }, []);

  // Load available input devices, validate saved selection, and auto-select from history
  useEffect(() => {
    const loadDevices = async () => {
      // Check if microphone permission is already granted before requesting
      let shouldRequestMic = true;

      try {
        // Use Permissions API to check status without prompting (Chrome/Firefox)
        // Safari doesn't support querying 'microphone', so we catch and fall through
        if (navigator.permissions && navigator.permissions.query) {
          const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
          if (permissionStatus.state === 'granted') {
            console.log('[loadDevices] Mic permission already granted, skipping getUserMedia');
            shouldRequestMic = false;
          } else {
            console.log('[loadDevices] Mic permission status:', permissionStatus.state);
          }
        }
      } catch (err) {
        // Permissions API doesn't support 'microphone' (Safari) - continue with getUserMedia
        console.log('[loadDevices] Permissions API check failed:', err.message);
      }

      // Request mic permission to get device labels (browsers hide labels without permission)
      if (shouldRequestMic) {
        try {
          const { getMicrophoneStream, releaseStream } = await import('../services/microphoneService');
          await getMicrophoneStream();
          // Release immediately - we only needed permission to get device labels
          // Mic will be re-acquired when actually needed (voice trigger or recording)
          releaseStream();
          console.log('[loadDevices] Mic permission granted, stream released');
        } catch (err) {
          console.log('[loadDevices] Microphone permission not granted:', err.message);
        }
      }

      const devices = await getInputDevices();
      setInputDevices(devices);

      // First check if we have a locally saved device ID
      const savedDeviceId = settings.selectedInputDevice.get();
      if (savedDeviceId) {
        const deviceExists = devices.some(d => d.deviceId === savedDeviceId);
        if (deviceExists) {
          console.log('Restored saved input device:', savedDeviceId);
          return; // Already have a valid selection
        } else {
          console.log('Saved device no longer available, clearing selection');
          setSelectedDeviceId(null);
          settings.selectedInputDevice.set('');
        }
      }

      // Try to auto-select from device history (backend-synced)
      try {
        const userSettings = await fetchUserSettings();
        const deviceHistory = userSettings.inputDeviceHistory || [];

        if (deviceHistory.length > 0) {
          // Find a device from history that's currently connected (match by label)
          for (const historyLabel of deviceHistory) {
            const matchedDevice = devices.find(d => d.label === historyLabel);
            if (matchedDevice) {
              console.log('Auto-selected device from history:', matchedDevice.label);
              setSelectedDeviceId(matchedDevice.deviceId);
              settings.selectedInputDevice.set(matchedDevice.deviceId);
              return;
            }
          }
          console.log('No devices from history are currently connected');
        }
      } catch (err) {
        console.log('Could not load device history from backend:', err.message);
      }
    };
    loadDevices();

    // Listen for device changes (e.g., plugging in a new microphone)
    const handleDeviceChange = () => {
      loadDevices();
    };
    navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange);
    return () => navigator.mediaDevices?.removeEventListener('devicechange', handleDeviceChange);
  }, []);

  // Play/pause segment audio
  const handlePlaySegment = async (segmentId) => {
    console.log('[VehicleInput] handlePlaySegment called:', {
      segmentId,
      playingSegmentId,
      audioRefExists: !!audioRef.current,
      audioDatasetSegmentId: audioRef.current?.dataset?.segmentId,
      audioCurrentTime: audioRef.current?.currentTime,
      audioPaused: audioRef.current?.paused
    });

    // If this segment is currently playing, pause it (keep position)
    if (playingSegmentId === segmentId) {
      if (audioRef.current) {
        audioRef.current.pause();
        console.log('[VehicleInput] Paused at position:', audioRef.current.currentTime);
        // Keep audio element and URL alive for resume
      }
      setPlayingSegmentId(null);
      return;
    }

    // If this segment was paused (audio exists for same segment), resume it
    // Note: dataset values are always strings, so convert segmentId for comparison
    if (audioRef.current && audioRef.current.dataset?.segmentId === String(segmentId)) {
      console.log('[VehicleInput] Attempting to resume from position:', audioRef.current.currentTime);
      try {
        await audioRef.current.play();
        setPlayingSegmentId(segmentId);
        console.log('[VehicleInput] Resumed successfully');
        return;
      } catch (err) {
        console.error('[VehicleInput] Error resuming playback:', err);
        // Fall through to create new audio element
      }
    }

    // Stop any currently playing/paused audio from different segment
    if (audioRef.current) {
      console.log('[VehicleInput] Cleaning up previous audio');
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }

    try {
      // Check if this is a remote segment (from API, not local IndexedDB)
      const segment = segments.find(s => s.id === segmentId);
      console.log('[VehicleInput] handlePlaySegment - segmentId:', segmentId, 'segment:', segment);

      let url;
      let isPresignedUrl = false;

      if (segment?.isRemote) {
        // Remote segment - use presigned URL if available
        if (segment.audioUrl) {
          console.log('[VehicleInput] Playing remote segment with presigned URL:', segmentId);
          url = segment.audioUrl;
          isPresignedUrl = true;
        } else {
          console.log('[VehicleInput] Remote segment has no audioUrl:', segmentId);
          return;
        }
      } else {
        // Local segment - get blob from IndexedDB
        console.log('[VehicleInput] Getting local chunk from IndexedDB:', segmentId);
        const chunk = await getChunkById(segmentId);
        console.log('[VehicleInput] Got chunk:', chunk ? { id: chunk.id, blobSize: chunk.blob?.size, blobType: chunk.blobType } : null);
        if (chunk?.blob) {
          url = URL.createObjectURL(chunk.blob);
          audioUrlRef.current = url;
          console.log('[VehicleInput] Created blob URL:', url);
        } else {
          console.log('[VehicleInput] Local segment has no blob:', segmentId);
          return;
        }
      }

      if (url) {
        const audio = new Audio(url);
        audio.dataset.segmentId = String(segmentId);
        audio.dataset.isPresignedUrl = isPresignedUrl ? 'true' : 'false';
        audioRef.current = audio;
        if (!isPresignedUrl) {
          audioUrlRef.current = url;
        }

        // Log when audio metadata is loaded
        audio.onloadedmetadata = () => {
          console.log('[VehicleInput] Audio metadata loaded - duration:', audio.duration, 'readyState:', audio.readyState);
        };

        audio.onplay = () => {
          console.log('[VehicleInput] Audio onplay event fired');
        };

        audio.onpause = () => {
          console.log('[VehicleInput] Audio onpause event fired');
        };

        audio.onerror = (e) => {
          console.error('[VehicleInput] Audio playback error:', e, audio.error);
          setPlayingSegmentId(null);
          // Only revoke blob URLs, not presigned URLs
          if (!isPresignedUrl && audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
          }
        };

        audio.onended = () => {
          setPlayingSegmentId(null);
          // Only revoke blob URLs, not presigned URLs
          if (!isPresignedUrl && audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
          }
        };

        // Ensure volume is set
        audio.volume = 1.0;
        console.log('[VehicleInput] Calling audio.play()... volume:', audio.volume, 'muted:', audio.muted);
        await audio.play();
        console.log('[VehicleInput] Audio playback started successfully, currentTime:', audio.currentTime);
        setPlayingSegmentId(segmentId);
      }
    } catch (err) {
      console.error('[VehicleInput] Error playing segment:', err);
      setPlayingSegmentId(null);
    }
  };

  // Delete individual segment
  const handleDeleteSegment = async (segmentId) => {
    setDeletingSegmentId(segmentId);
    try {
      // Stop if playing
      if (playingSegmentId === segmentId) {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        setPlayingSegmentId(null);
      }

      // Find the segment to check if it's remote
      const segment = segments.find(s => s.id === segmentId);

      if (segment?.isRemote && segment?.filename) {
        // Remote segment - delete via API
        const inspection = getCurrentInspection();
        if (inspection?.inspectionId) {
          console.log('[VehicleInput] Deleting remote segment:', segment.filename);
          await deleteSegment(inspection.inspectionId, segment.filename);
        }
      } else {
        // Local segment - delete from IndexedDB
        await deleteChunkById(segmentId);
      }

      await loadSegments();
    } catch (err) {
      console.error('Error deleting segment:', err);
      setError(t('recording.delete_segment_error'));
    } finally {
      setDeletingSegmentId(null);
    }
  };

  // Handle delete all (entire inspection) and go back to home
  const handleDeleteAll = async () => {
    const inspection = getCurrentInspection();
    if (!inspection?.inspectionId) {
      setShowDeleteAllDialog(false);
      return;
    }

    setIsDeleting(true);
    try {
      // Stop any playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
      setPlayingSegmentId(null);

      // Try to delete from backend
      try {
        await deleteInspection(inspection.inspectionId);
      } catch (err) {
        console.log('Backend delete failed (inspection may not exist yet):', err.message);
      }

      // Clear local session data
      await clearSession(inspection.inspectionId);

      // Go back to home
      onBack?.();
    } catch (err) {
      console.error('Error deleting inspection:', err);
      setError(t('recording.delete_error'));
      setShowDeleteAllDialog(false);
    } finally {
      setIsDeleting(false);
    }
  };

  // Format timestamp for display (date + time in German format)
  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Use simplified setting hooks
  const [shakeEnabled] = useSetting('shake');
  const [bluetoothButtonEnabled] = useSetting('bluetooth');
  const [mediaButtonEnabled] = useSetting('mediaButton');
  const { enabled: voiceTriggerEnabled, triggerWord } = useVoiceTriggerSetting();
  const [audioUploadEnabled] = useAudioUploadSetting();
  const [extendedErrorReportsEnabled] = useExtendedErrorReportsSetting();

  // Voice activation state
  const [voiceListening, setVoiceListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [lastHeardText, setLastHeardText] = useState('');
  const recognitionRef = useRef(null);
  const hasTriggeredRef = useRef(false);
  const isMountedRef = useRef(true);
  const triggerRecordingRef = useRef(null);
  const restartTimeoutRef = useRef(null); // Track pending restarts to prevent race conditions
  const restartCountRef = useRef(0); // Track restart count for exponential backoff on Android
  const isPausedForPlaybackRef = useRef(false); // Prevent voice restart during audio playback

  // Vosk-related state (Android only)
  const [voskAudioContext, setVoskAudioContext] = useState(null);
  const [voskSourceNode, setVoskSourceNode] = useState(null);
  const [voskModelLoadProgress, setVoskModelLoadProgress] = useState(null);
  const voskStreamRef = useRef(null);
  const useVoskForTrigger = shouldUseVosk(); // True on Android

  // Voice debug log (visible when Extended Error Reports enabled)
  const [voiceDebugLog, setVoiceDebugLog] = useState([]);
  const voiceDebugIdRef = useRef(0);

  const addVoiceDebugEntry = useCallback((type, message, data = {}) => {
    if (!extendedErrorReportsEnabled) return;

    const timestamp = new Date().toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
    voiceDebugIdRef.current += 1;
    const entry = {
      id: `${Date.now()}-${voiceDebugIdRef.current}`,
      timestamp,
      type, // 'transcript', 'match', 'trigger', 'error', 'status'
      message,
      ...data
    };
    setVoiceDebugLog(prev => [entry, ...prev].slice(0, 100)); // Keep last 100 entries
  }, [extendedErrorReportsEnabled]);

  // Log component mount for debugging
  useEffect(() => {
    console.log('[VehicleInput] Component MOUNTED with initialVehicleId:', initialVehicleId);
    return () => {
      console.log('[VehicleInput] Component UNMOUNTED');
    };
  }, [initialVehicleId]);

  // Check if Web Speech API is supported
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const isSupported = !!SpeechRecognition;
    setVoiceSupported(isSupported);

    // Log browser info for debugging
    if (isSupported) {
      const ua = navigator.userAgent;
      const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
      const isIOS = /iPad|iPhone|iPod/.test(ua);
      console.log('Speech Recognition browser info:', {
        supported: true,
        isSafari,
        isIOS,
        userAgent: ua.substring(0, 100)
      });
      if (isSafari || isIOS) {
        console.warn('Note: Safari/iOS have limited Web Speech API support. Consider using Chrome for better voice recognition.');
      }
    }
  }, []);

  // Start recording callback
  // Save device label to history when used for recording
  const saveDeviceToHistory = useCallback(async (deviceId) => {
    if (!deviceId) return;

    const device = inputDevices.find(d => d.deviceId === deviceId);
    if (!device || !device.label) return;

    try {
      const userSettings = await fetchUserSettings();
      const currentHistory = userSettings.inputDeviceHistory || [];

      // Add to front of list, remove duplicates, limit to 10
      const newHistory = [device.label, ...currentHistory.filter(l => l !== device.label)].slice(0, 10);

      if (JSON.stringify(newHistory) !== JSON.stringify(currentHistory)) {
        await updateUserSettings({ inputDeviceHistory: newHistory });
        console.log('Device saved to history:', device.label);
      }
    } catch (err) {
      console.log('Could not save device to history:', err.message);
    }
  }, [inputDevices]);

  const triggerRecording = useCallback((detectedPhrase = null) => {
    // Log specifically if this looks like a media button trigger
    const isMediaButtonTrigger = ['play', 'pause', 'stop'].includes(detectedPhrase);
    if (isMediaButtonTrigger) {
      console.log('[MediaButton] triggerRecording called with action:', detectedPhrase, new Error().stack);
    }
    console.log('[triggerRecording] Called. hasTriggered:', hasTriggeredRef.current, 'isVehicleIdValid:', isVehicleIdValid, 'vehicleId:', vehicleId, 'isMediaButton:', isMediaButtonTrigger, 'isPausedForPlayback:', isPausedForPlaybackRef.current);

    // Ignore triggers during audio playback (prevents accidental triggers from playback audio)
    if (isPausedForPlaybackRef.current) {
      console.log('[triggerRecording] Audio playing, ignoring trigger to prevent false positives');
      return;
    }

    if (hasTriggeredRef.current) {
      console.log('[triggerRecording] Already triggered, skipping');
      return;
    }
    if (isVehicleIdValid) {
      hasTriggeredRef.current = true;
      // Capture timestamp and detected phrase for start phrase trimming (voice trigger only)
      const startTriggerTimestamp = detectedPhrase ? Date.now() : null;
      // Generate title (vehicleId if provided, else auto-generated)
      const title = generateInspectionTitle(vehicleId, i18n.language);
      console.log('[triggerRecording] Recording with vehicleId:', vehicleId.trim() || '(empty)', 'title:', title, 'timestamp:', startTriggerTimestamp, 'phrase:', detectedPhrase);
      // Save device to history if a specific device is selected
      if (selectedDeviceId) {
        saveDeviceToHistory(selectedDeviceId);
      }
      // vehicleId is already formatted by formatGermanPlate (uppercase, trimmed)
      // Pass start trigger info for trimming (timestamp + phrase for duration calculation)
      onStartRecording(vehicleId.trim() || null, selectedDeviceId, { timestamp: startTriggerTimestamp, phrase: detectedPhrase, title });
    }
  }, [vehicleId, isVehicleIdValid, onStartRecording, selectedDeviceId, saveDeviceToHistory, i18n.language]);

  // Keep ref in sync with latest triggerRecording (avoids stale closure in speech recognition)
  triggerRecordingRef.current = triggerRecording;

  // Keep ref in sync with latest addVoiceDebugEntry (avoids stale closure in speech recognition)
  const addVoiceDebugEntryRef = useRef(addVoiceDebugEntry);
  addVoiceDebugEntryRef.current = addVoiceDebugEntry;

  // Vosk audio setup for Android voice trigger
  // Creates standalone mic capture since we're not recording yet
  useEffect(() => {
    if (!useVoskForTrigger || !voiceTriggerEnabled) {
      return;
    }

    let isActive = true;
    let audioCtx = null;
    let stream = null;

    const setupVoskAudio = async () => {
      try {
        console.log('[VehicleInput/Vosk] Setting up audio context for voice trigger...');
        addVoiceDebugEntryRef.current?.('status', 'Setting up Vosk audio...');

        // Get mic stream
        stream = await getMicrophoneStream(selectedDeviceId);
        if (!isActive) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        voskStreamRef.current = stream;

        // Create AudioContext
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        if (audioCtx.state === 'suspended') {
          await audioCtx.resume();
        }

        // Create source node from mic stream
        const source = audioCtx.createMediaStreamSource(stream);

        if (!isActive) {
          audioCtx.close();
          return;
        }

        setVoskAudioContext(audioCtx);
        setVoskSourceNode(source);
        console.log('[VehicleInput/Vosk] Audio context ready, sampleRate:', audioCtx.sampleRate);
        addVoiceDebugEntryRef.current?.('status', `Vosk audio ready (${audioCtx.sampleRate}Hz)`);

      } catch (err) {
        console.error('[VehicleInput/Vosk] Failed to setup audio:', err);
        addVoiceDebugEntryRef.current?.('error', `Audio setup failed: ${err.message}`);
      }
    };

    setupVoskAudio();

    return () => {
      console.log('[VehicleInput/Vosk] Cleaning up audio context...');
      isActive = false;

      if (voskStreamRef.current) {
        voskStreamRef.current.getTracks().forEach(t => t.stop());
        voskStreamRef.current = null;
      }

      if (audioCtx && audioCtx.state !== 'closed') {
        audioCtx.close().catch(() => {});
      }

      setVoskAudioContext(null);
      setVoskSourceNode(null);
    };
  }, [useVoskForTrigger, voiceTriggerEnabled, selectedDeviceId]);

  // Vosk voice trigger hook (Android only)
  const handleVoskKeywordDetected = useCallback((keyword, timestamp) => {
    console.log('[VehicleInput/Vosk] Keyword detected:', keyword);
    addVoiceDebugEntryRef.current?.('trigger', `TRIGGERED via Vosk! Matched: "${keyword}"`);
    triggerRecordingRef.current?.(keyword);
  }, []);

  const {
    isListening: isVoskListening,
    // isModelReady: isVoskModelReady, // May be used for future UI enhancement
    isModelLoading: isVoskModelLoading,
    // lastHeardText: voskLastHeardText, // Transcript is set via onTranscript callback
    // error: voskError // Error handling via onError callback
  } = useVoskVoiceTrigger({
    enabled: useVoskForTrigger && voiceTriggerEnabled && !hasTriggeredRef.current && voskAudioContext !== null,
    audioContext: voskAudioContext,
    sourceNode: voskSourceNode,
    keywords: settings.voiceTrigger.getKeywords(),
    onKeywordDetected: handleVoskKeywordDetected,
    onModelLoadProgress: setVoskModelLoadProgress,
    onTranscript: (text, isFinal) => {
      if (isFinal) {
        setLastHeardText(text);
      }
      addVoiceDebugEntryRef.current?.(
        'transcript',
        `[Vosk] "${text}"`,
        { isFinal }
      );
    },
    onError: (err) => {
      console.error('[VehicleInput/Vosk] Recognition error:', err);
      addVoiceDebugEntryRef.current?.('error', `Vosk error: ${err.message}`);
    }
  });

  // Combine Vosk and Web Speech API listening state
  const isVoiceActive = useVoskForTrigger ? isVoskListening : voiceListening;
  const isVoiceModelLoading = useVoskForTrigger ? isVoskModelLoading : false;

  // Initialize and start voice recognition
  const startVoiceRecognition = useCallback(() => {
    console.log('[startVoiceRecognition] Called. isMounted:', isMountedRef.current, 'hasTriggered:', hasTriggeredRef.current);
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.log('[startVoiceRecognition] SpeechRecognition not supported');
      return;
    }

    // On Android, release the cached mic stream to avoid conflict with speech recognition
    // The stream will be re-acquired when entering Recording
    const isAndroid = /android/i.test(navigator.userAgent);
    if (isAndroid) {
      console.log('[startVoiceRecognition] Android detected - releasing cached mic stream');
      releaseStream();
    }
    // Note on desktop: Don't release the cached mic stream - Recording will use it
    // The fan-out pattern in audioStreamService handles sharing between recording and speech recognition

    // Clear any pending restart timeout to prevent race conditions
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }

    if (recognitionRef.current) {
      console.log('[startVoiceRecognition] Stopping existing recognition');
      try {
        recognitionRef.current.stop();
      } catch (e) { /* ignore */ }
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = settings.triggerLanguage.get();
    recognition.maxAlternatives = 3;

    recognition.onstart = () => {
      const keywords = settings.voiceTrigger.getKeywords();
      console.log('Voice recognition started, listening for:', keywords);
      setVoiceListening(true);
      // Note: Don't reset restartCountRef here - let it accumulate for backoff
      // It will be reset when trigger phrase is detected or component unmounts
      addVoiceDebugEntryRef.current('status', `Started listening (lang: ${recognition.lang})`, { keywords: keywords.join(', ') });
      // Note: Don't start audio level monitor here - it creates a competing
      // getUserMedia stream that can prevent speech recognition from receiving audio
    };

    recognition.onaudiostart = () => {
      console.log('Audio capturing started');
      addVoiceDebugEntryRef.current('status', 'Audio capture started');
    };

    recognition.onaudioend = () => {
      console.log('Audio capturing ended');
      addVoiceDebugEntryRef.current('status', 'Audio capture ended');
    };

    recognition.onsoundstart = () => {
      console.log('Sound detected');
      addVoiceDebugEntryRef.current('status', 'Sound detected');
    };

    recognition.onsoundend = () => {
      console.log('Sound ended');
      addVoiceDebugEntryRef.current('status', 'Sound ended');
    };

    recognition.onspeechstart = () => {
      console.log('Speech detected!');
      addVoiceDebugEntryRef.current('status', 'Speech detected');
    };

    recognition.onspeechend = () => {
      console.log('Speech ended');
      addVoiceDebugEntryRef.current('status', 'Speech ended');
    };

    recognition.onnomatch = () => {
      console.log('No speech match');
      addVoiceDebugEntryRef.current('status', 'No match (speech not recognized)');
    };

    recognition.onresult = (event) => {
      const lastResult = event.results[event.results.length - 1];
      const transcript = lastResult[0].transcript.toLowerCase().trim();
      const confidence = lastResult[0].confidence;
      const isFinal = lastResult.isFinal;

      // Only update state on final results to prevent render thrashing
      if (isFinal) {
        setLastHeardText(transcript);
        console.log(`Voice heard (final): "${transcript}" (confidence: ${(confidence * 100).toFixed(1)}%)`);
      }

      const keywords = settings.voiceTrigger.getKeywords();
      // Find which keyword matched (for accurate trim duration calculation)
      const matchedKeyword = keywords.find(keyword => phraseMatches(transcript, keyword));

      // Debug log: show what was heard and if it matched
      addVoiceDebugEntryRef.current(
        matchedKeyword ? 'match' : 'transcript',
        `"${transcript}"`,
        {
          confidence: `${(confidence * 100).toFixed(1)}%`,
          isFinal,
          matchedKeyword: matchedKeyword || null
        }
      );

      if (matchedKeyword && !hasTriggeredRef.current) {
        console.log(`>>> TRIGGERING RECORDING <<< (matched: "${matchedKeyword}")`);
        addVoiceDebugEntryRef.current('trigger', `TRIGGERED! Matched: "${matchedKeyword}"`, { transcript });
        // Pass the matched keyword for accurate trim duration
        triggerRecordingRef.current?.(matchedKeyword);
      }
    };

    recognition.onerror = (event) => {
      console.log('Speech recognition error:', event.error);
      addVoiceDebugEntryRef.current('error', `Error: ${event.error}`);
      // Don't restart from onerror - let onend handle it to avoid race conditions
      // onerror is always followed by onend
    };

    recognition.onend = () => {
      console.log('Speech recognition ended. hasTriggered:', hasTriggeredRef.current, 'isMounted:', isMountedRef.current, 'pausedForPlayback:', isPausedForPlaybackRef.current);
      addVoiceDebugEntryRef.current('status', 'Recognition ended');

      // Only restart if we haven't triggered, component is still mounted, and not paused for playback
      if (!hasTriggeredRef.current && isMountedRef.current && !isPausedForPlaybackRef.current) {
        // Clear any existing timeout before scheduling new one
        if (restartTimeoutRef.current) {
          clearTimeout(restartTimeoutRef.current);
        }

        // Use exponential backoff on Android to avoid beeping from rapid mic acquire/release
        // Start with longer delays to minimize system sounds
        const isAndroid = /android/i.test(navigator.userAgent);
        const BACKOFF_DELAYS_ANDROID = [2000, 3000, 4000, 5000, 5000];
        const MAX_RESTART_RETRIES = 10;

        // Check if we've exceeded max retries
        if (isAndroid && restartCountRef.current >= MAX_RESTART_RETRIES) {
          console.warn('[VoiceRecognition] Max retries reached on Android, stopping');
          addVoiceDebugEntryRef.current('warning', 'Max retries reached');
          setVoiceListening(false);
          return;
        }

        // Calculate delay - longer on Android with backoff
        const delay = isAndroid
          ? BACKOFF_DELAYS_ANDROID[Math.min(restartCountRef.current, BACKOFF_DELAYS_ANDROID.length - 1)]
          : 100;

        restartCountRef.current++;

        restartTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current && !hasTriggeredRef.current && !isPausedForPlaybackRef.current) {
            console.log(`Restarting speech recognition (attempt ${restartCountRef.current}, delay ${delay}ms)...`);
            addVoiceDebugEntryRef.current('status', `Restarting (${restartCountRef.current})...`);
            startVoiceRecognition();
          }
        }, delay);
      } else {
        setVoiceListening(false);
      }
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch (e) {
      console.log('Voice recognition start failed, retrying...', e);
      // Clear any existing timeout before scheduling new one
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      restartTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current && !hasTriggeredRef.current) {
          startVoiceRecognition();
        }
      }, 1000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // No deps - uses refs for callbacks to avoid restart loops

  // Auto-start voice recognition when component mounts (only if enabled)
  // On Android, Vosk handles voice trigger instead of Web Speech API
  useEffect(() => {
    console.log('[VoiceEffect] Voice trigger check:', { voiceSupported, voiceTriggerEnabled, vehicleId, useVoskForTrigger });

    // On Android, Vosk handles voice recognition - skip Web Speech API
    if (useVoskForTrigger) {
      console.log('[VoiceEffect] Using Vosk on Android, skipping Web Speech API');
      isMountedRef.current = true;
      hasTriggeredRef.current = false;
      return;
    }

    if (voiceSupported && voiceTriggerEnabled) {
      console.log('[VoiceEffect] Starting voice recognition for start trigger...');
      console.log('[VoiceEffect] Resetting refs - isMounted: true, hasTriggered: false, restartCount: 0');
      isMountedRef.current = true;
      hasTriggeredRef.current = false;
      restartCountRef.current = 0; // Reset backoff counter on fresh start
      setLastHeardText('');

      const timer = setTimeout(() => {
        if (isMountedRef.current) {
          startVoiceRecognition();
        }
      }, 500);

      return () => {
        console.log('[VoiceEffect] Cleanup - stopping voice recognition');
        isMountedRef.current = false;
        clearTimeout(timer);
        // Clear any pending restart timeout
        if (restartTimeoutRef.current) {
          clearTimeout(restartTimeoutRef.current);
          restartTimeoutRef.current = null;
        }
        if (recognitionRef.current) {
          try {
            recognitionRef.current.stop();
            recognitionRef.current = null;
          } catch (e) { /* ignore */ }
        }
        setVoiceListening(false);
      };
    }
  }, [voiceSupported, voiceTriggerEnabled, startVoiceRecognition, useVoskForTrigger]);

  // Mute voice triggers during audio playback (but keep recognition running to avoid mic release beeps)
  useEffect(() => {
    if (playingSegmentId !== null) {
      // Audio is playing - mute triggers but keep voice recognition running
      // This prevents accidental triggers from playback audio containing the trigger word
      // and avoids the OS beep from mic release/reacquire
      console.log('[VoicePlayback] Audio playing, muting voice triggers');
      isPausedForPlaybackRef.current = true;
    } else {
      // Playback ended - unmute triggers
      if (isPausedForPlaybackRef.current) {
        console.log('[VoicePlayback] Playback ended, unmuting voice triggers');
        isPausedForPlaybackRef.current = false;
      }
    }
  }, [playingSegmentId]);

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      // Check if it's an audio file
      if (file.type.startsWith('audio/') || file.name.match(/\.(mp3|wav|m4a|ogg|webm|aac|flac)$/i)) {
        if (vehicleId.trim().length >= 3) {
          onFileUpload?.(file, vehicleId.trim().toUpperCase());
        } else {
          setError(t('recording.vehicle_id_min_length'));
        }
      } else {
        setError('Please drop an audio file (mp3, wav, m4a, etc.)');
      }
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (vehicleId.trim().length >= 3) {
        onFileUpload?.(file, vehicleId.trim().toUpperCase());
      } else {
        setError(t('recording.vehicle_id_min_length'));
      }
      // Reset the input so the same file can be selected again if needed
      e.target.value = '';
    }
  };

  // Shake detection hook - uses triggerRecording for consistency
  useShakeDetection(shakeEnabled, triggerRecording);

  // Bluetooth button detection hook
  useBluetoothButton(bluetoothButtonEnabled, triggerRecording);

  // Media button (headset play/pause) detection hook
  useMediaButton(mediaButtonEnabled, triggerRecording);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // vehicleId is optional - but if provided, it must pass validation
    if (!isVehicleIdValid) {
      setError(validation.error || t('recording.vehicle_id_min_length'));
      return;
    }

    // Save device to history if a specific device is selected
    if (selectedDeviceId) {
      saveDeviceToHistory(selectedDeviceId);
    }
    // Generate title (vehicleId if provided, else auto-generated)
    const title = generateInspectionTitle(vehicleId, i18n.language);
    // Use the already-formatted vehicleId (formatGermanPlate handles uppercase and trimming)
    onStartRecording(vehicleId.trim() || null, selectedDeviceId, { title });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* App Bar */}
      <header className="app-bar">
        <button onClick={onBack} className="cursor-pointer hover:opacity-80 mr-2">
          <ArrowLeft size={24} className="text-white" />
        </button>
        <Logo size={40} />
        <h1 className="flex-1 text-white font-normal text-xl ml-2">{vehicleId || storedTitle || t('recording.new_inspection')}</h1>
        <AppBarMenu onLogout={onLogout} isAdmin={isAdmin} />
      </header>

      {/* Main Content */}
      <main className="w-full px-4 lg:px-8 py-8">
        <div className="card-elevated p-6">
          {/* Voice Activation Status - only show when voice trigger is enabled */}
          {voiceTriggerEnabled && (
            <Alert
              severity={(!voiceSupported && !useVoskForTrigger) ? "warning" : isVoiceActive ? "success" : "info"}
              icon={isVoiceActive ? Ear : Mic}
            >
              <div className="flex-1">
                <p className="font-semibold">
                  {(!voiceSupported && !useVoskForTrigger)
                    ? t('recording.voice_not_supported')
                    : isVoiceModelLoading
                      ? t('recording.voice_model_loading', { percent: voskModelLoadProgress || 0 })
                      : isVoiceActive
                        ? t('recording.voice_active', { triggerWord })
                        : t('recording.voice_initializing')}
                </p>
                {lastHeardText && (
                  <p className="text-sm opacity-75 mt-1">
                    {t('recording.heard')}: "{lastHeardText}"
                  </p>
                )}
                {useVoskForTrigger && (
                  <p className="text-xs opacity-60 mt-1">
                    {t('recording.voice_engine_vosk')}
                  </p>
                )}
              </div>
            </Alert>
          )}

          {/* Voice Debug Log - only visible when Extended Error Reports enabled */}
          {extendedErrorReportsEnabled && voiceTriggerEnabled && (
            <div className="mt-4 p-3 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {t('recording.voice_debug_log')}
                </h3>
                {voiceDebugLog.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setVoiceDebugLog([])}
                    className="text-xs text-gray-500 hover:text-gray-300"
                  >
                    {t('common.clear')}
                  </button>
                )}
              </div>
              <div className="max-h-48 overflow-y-auto font-mono text-xs space-y-1">
                {voiceDebugLog.length === 0 ? (
                  <div className="text-gray-500 italic">Waiting for speech events...</div>
                ) : (
                  voiceDebugLog.map(entry => (
                    <div
                      key={entry.id}
                      className={`flex items-start gap-2 ${
                        entry.type === 'trigger' ? 'text-red-400 font-bold' :
                        entry.type === 'match' ? 'text-yellow-400' :
                        entry.type === 'error' ? 'text-red-500' :
                        entry.type === 'status' ? 'text-blue-400' :
                        'text-green-400'
                      }`}
                    >
                      <span className="text-gray-500 flex-shrink-0">[{entry.timestamp}]</span>
                      <span className="flex-1">
                        {entry.message}
                        {entry.confidence && <span className="text-gray-500 ml-2">({entry.confidence})</span>}
                        {entry.matchedKeyword && <span className="text-yellow-300 ml-2">→ matched: "{entry.matchedKeyword}"</span>}
                      </span>
                    </div>
                  ))
                )}
              </div>
              {voiceDebugLog.length > 0 && voiceDebugLog[0].keywords && (
                <div className="mt-2 pt-2 border-t border-gray-700 text-xs text-gray-500">
                  {t('recording.voice_keywords')}: {voiceDebugLog[0].keywords}
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className={voiceTriggerEnabled ? "mt-6" : ""}>
            {/* Vehicle ID Input */}
            <div className="mb-6">
              <label htmlFor="vehicleId" className="block text-sm font-medium text-text-muted mb-2">
                {t('recording.license_plate')}
              </label>
              <div className={`relative flex rounded-lg overflow-hidden border-2 ${validation.error ? 'border-red-400 bg-red-50' : (!isVehicleIdValid && !hasRecordedSegments) ? 'border-amber-300 bg-amber-50' : 'border-gray-300'}`}>
                {/* EU Blue Strip */}
                <div className="flex flex-col items-center justify-center bg-[#003399] text-white px-1.5 py-2 min-w-[36px] flex-shrink-0">
                  {/* EU Stars Circle - 12 stars arranged in a circle */}
                  <div className="relative w-[24px] h-[24px]">
                    {[...Array(12)].map((_, i) => {
                      const angle = (i * 30 - 90) * (Math.PI / 180);
                      const radius = 10;
                      const x = 12 + radius * Math.cos(angle) - 2;
                      const y = 12 + radius * Math.sin(angle) - 2;
                      return (
                        <span
                          key={i}
                          className="absolute text-yellow-400 text-[5px] leading-none"
                          style={{ left: `${x}px`, top: `${y}px` }}
                        >
                          ★
                        </span>
                      );
                    })}
                  </div>
                  {/* Country Code */}
                  <div className="text-white font-bold text-sm leading-none mt-2">D</div>
                </div>
                {/* License Plate Input - editable until transcribe is clicked */}
                <input
                  id="vehicleId"
                  type="text"
                  value={vehicleId}
                  onChange={handleVehicleIdChange}
                  onBlur={handleVehicleIdBlur}
                  placeholder="B-AB 1234"
                  autoFocus={!hasRecordedSegments}
                  className={`flex-1 text-center text-2xl font-bold font-mono tracking-wider py-3 px-4 border-0 outline-none placeholder:text-text-muted placeholder:font-normal bg-white ${validation.error ? 'bg-red-50' : ''}`}
                />
                {/* Right spacer to balance the EU blue strip for visual centering */}
                <div className="min-w-[36px] flex-shrink-0" />
              </div>
              {/* Validation error (blocking) */}
              {validation.error && !hasRecordedSegments && (
                <div className="mt-2 flex items-center gap-2 text-red-700 text-sm">
                  <AlertTriangle size={16} />
                  <p>{validation.error}</p>
                </div>
              )}
              {/* Validation warning (non-blocking format hint) */}
              {validation.warning && !hasRecordedSegments && !validation.error && (
                <div className="mt-2 flex items-center gap-2 text-amber-600 text-sm">
                  <AlertTriangle size={16} />
                  <p>{validation.warning}</p>
                </div>
              )}
              {/* Default hint when empty or too short */}
              {!isVehicleIdValid && !hasRecordedSegments && !validation.error && !validation.warning && (
                <div className="mt-2 flex items-center gap-2 text-amber-700 text-sm">
                  <AlertTriangle size={16} />
                  <p>{t('recording.vehicle_id_hint')}</p>
                </div>
              )}
              {/* Form submission error */}
              {error && (
                <p className="text-sm text-error mt-2">{error}</p>
              )}
            </div>

            {/* Input Device Selector - hidden, always use default mic */}

            {/* Start Recording Button - always visible for multi-modal input */}
            <button
              type="submit"
              className="w-full bg-primary hover:bg-primary-dark active:scale-[0.98] text-white font-bold text-2xl py-8 px-6 rounded-lg flex flex-col items-center gap-3 transition-colors cursor-pointer"
            >
              <Mic size={64} />
              <span>{hasRecordedSegments ? t('recording.continue_recording') : t('recording.start')}</span>
              {!isVehicleIdValid && (
                <span className="text-sm font-normal opacity-90">{t('recording.vehicle_id_optional')}</span>
              )}
            </button>
          </form>

          {/* Transcribe Button - shown when segments exist, enabled when at least one is uploaded */}
          {segments.length > 0 && (() => {
            const hasUploadedSegment = segments.some(s => s.status === ChunkStatus.UPLOADED);
            return (
              <button
                type="button"
                disabled={!hasUploadedSegment}
                onClick={async () => {
                  const inspection = getCurrentInspection();
                  if (inspection?.inspectionId) {
                    // If vehicleId was entered, update the inspection title before transcribing
                    const trimmedVehicleId = vehicleId?.trim();
                    if (trimmedVehicleId && trimmedVehicleId !== inspection.vehicleId) {
                      try {
                        console.log('[VehicleInput] Updating inspection with vehicleId:', trimmedVehicleId);
                        await updateInspection(inspection.inspectionId, {
                          vehicleId: trimmedVehicleId,
                          title: trimmedVehicleId
                        });
                      } catch (err) {
                        console.error('[VehicleInput] Failed to update inspection:', err);
                        // Continue with transcribe even if update fails
                      }
                    }
                    onTranscribe?.(inspection.inspectionId);
                  }
                }}
                className={`w-full mt-4 text-white font-bold text-2xl py-8 px-6 rounded-lg flex flex-col items-center gap-3 transition-colors ${
                  hasUploadedSegment
                    ? 'bg-secondary hover:bg-secondary-dark active:scale-[0.98] cursor-pointer'
                    : 'bg-gray-400 cursor-not-allowed opacity-60'
                }`}
              >
                <Sparkles size={64} />
                <span>{t('recording.transcribe')}</span>
                {!hasUploadedSegment && (
                  <span className="text-sm font-normal opacity-80">{t('recording.waiting_for_upload')}</span>
                )}
              </button>
            );
          })()}

        </div>

        {/* Segments Table - shown when segments exist */}
        {segments.length > 0 && (
          <div className="card-elevated mt-6 p-4">
            <h3 className="text-sm font-medium text-text-muted mb-3">{t('recording.segments_title')}</h3>

            {/* Segments Table */}
            <div className="overflow-x-auto max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-2 font-medium text-text-muted">{t('recording.segment_time')}</th>
                    <th className="text-left py-2 px-2 font-medium text-text-muted">{t('recording.duration_raw')}</th>
                    <th className="text-left py-2 px-2 font-medium text-text-muted">{t('recording.duration_processed')}</th>
                    <th className="text-center py-2 px-2 font-medium text-text-muted">{t('recording.segment_status')}</th>
                    <th className="text-center py-2 px-2 font-medium text-text-muted">{t('recording.segment_actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {segments.map((segment) => (
                    <React.Fragment key={segment.id}>
                      <tr className="border-b border-border last:border-0">
                        <td className="py-2 px-2 font-mono text-xs">{formatDateTime(segment.timestamp)}</td>
                        <td className="py-2 px-2">{formatDuration(segment.originalDuration || segment.duration || 0)}</td>
                        <td className="py-2 px-2">{formatDuration(segment.duration || 0)}</td>
                        <td className="py-2 px-2 text-center">
                          {segment.status === ChunkStatus.UPLOADED ? (
                            <Check size={18} className="inline text-green-600" />
                          ) : segment.status === ChunkStatus.UPLOADING ? (
                            <Loader2 size={18} className="inline text-blue-600 animate-spin" />
                          ) : segment.status === ChunkStatus.FAILED ? (
                            <X size={18} className="inline text-error" />
                          ) : (
                            <Loader2 size={18} className="inline text-text-muted animate-spin" />
                          )}
                        </td>
                        <td className="py-2 px-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handlePlaySegment(segment.id)}
                              disabled={segment.isRemote && !segment.audioUrl}
                              className={`p-4 rounded-xl transition-colors min-w-[56px] min-h-[56px] flex items-center justify-center ${
                                segment.isRemote && !segment.audioUrl
                                  ? 'opacity-40 cursor-not-allowed'
                                  : 'hover:bg-surface-alt active:bg-surface-alt'
                              }`}
                              title={segment.isRemote && !segment.audioUrl ? t('recording.playback_not_available') : (playingSegmentId === segment.id ? t('common.pause') : t('recording.play_segment'))}
                            >
                              {playingSegmentId === segment.id ? (
                                <Pause size={28} className="text-primary" fill="currentColor" />
                              ) : (
                                <Play size={28} className={segment.isRemote && !segment.audioUrl ? 'text-text-muted' : 'text-primary'} />
                              )}
                            </button>
                            <button
                              onClick={() => handleDeleteSegment(segment.id)}
                              disabled={deletingSegmentId === segment.id}
                              className="p-4 rounded-xl transition-colors min-w-[56px] min-h-[56px] flex items-center justify-center hover:bg-red-50 active:bg-red-100 disabled:opacity-50"
                              title={t('recording.delete_segment')}
                            >
                              {deletingSegmentId === segment.id ? (
                                <Loader2 size={28} className="text-error animate-spin" />
                              ) : (
                                <Trash2 size={28} className="text-error" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Error details row - shown when extended error reports is enabled */}
                      {extendedErrorReportsEnabled && segment.status === ChunkStatus.FAILED && (segment.error || segment.errorMessage) && (
                        <tr className="bg-red-50 dark:bg-red-900/20">
                          <td colSpan={5} className="py-2 px-3">
                            <div className="text-xs text-error">
                              <span className="font-semibold">{t('recording.error_details')}:</span>{' '}
                              <span className="font-mono">{segment.error || segment.errorMessage}</span>
                              {(segment.errorTimestamp || segment.timestamp) && (
                                <span className="text-text-muted ml-2">
                                  ({new Date(segment.errorTimestamp || segment.timestamp).toLocaleString()})
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      {/* Stale segment warning - shown when segment is stuck in PENDING/UPLOADING for > 60s */}
                      {extendedErrorReportsEnabled &&
                        (segment.status === ChunkStatus.PENDING || segment.status === ChunkStatus.UPLOADING) &&
                        segment.timestamp && (Date.now() - segment.timestamp > 60000) && (
                        <tr className="bg-amber-50 dark:bg-amber-900/20">
                          <td colSpan={5} className="py-2 px-3">
                            <div className="text-xs text-amber-700 dark:text-amber-400">
                              <span className="font-semibold">{t('recording.stale_segment_warning')}:</span>{' '}
                              <span className="font-mono">
                                {t('recording.stale_segment_message', {
                                  minutes: Math.floor((Date.now() - segment.timestamp) / 60000)
                                })}
                              </span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Total Duration */}
            <div className="mt-3 pt-3 border-t border-border text-right text-sm text-text-muted">
              {t('recording.total_duration', {
                duration: formatDuration(segments.reduce((sum, s) => sum + (s.duration || 0), 0))
              })}
            </div>

            {/* Delete All Button */}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowDeleteAllDialog(true)}
                className="w-full btn-secondary flex items-center justify-center gap-2 border-error text-error hover:bg-red-50 py-3"
              >
                <Trash2 size={20} />
                <span>{t('recording.delete_all')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Delete Button - shown for active sessions without segments */}
        {getCurrentInspection()?.inspectionId && segments.length === 0 && (
          <div className="card-elevated mt-6 p-4">
            <p className="text-sm text-text-muted mb-4">{t('recording.no_segments')}</p>
            <button
              type="button"
              onClick={() => setShowDeleteAllDialog(true)}
              className="w-full btn-secondary flex items-center justify-center gap-2 border-error text-error hover:bg-red-50"
            >
              <Trash2 size={20} />
              <span>{t('recording.delete_inspection')}</span>
            </button>
          </div>
        )}

        {/* Audio File Upload Drop Zone - developer feature, only when enabled */}
        {audioUploadEnabled && (() => {
          const isVehicleIdValid = vehicleId.trim().length >= 3;
          return (
            <div
              className={`mt-6 border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                !isVehicleIdValid
                  ? 'border-border opacity-50 cursor-not-allowed'
                  : isDragging
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'
              }`}
              onDragEnter={isVehicleIdValid ? handleDragEnter : undefined}
              onDragLeave={isVehicleIdValid ? handleDragLeave : undefined}
              onDragOver={isVehicleIdValid ? handleDragOver : (e) => e.preventDefault()}
              onDrop={isVehicleIdValid ? handleDrop : (e) => { e.preventDefault(); setError(t('recording.vehicle_id_min_length')); }}
            >
              <Upload size={48} className={`mx-auto mb-3 ${isDragging && isVehicleIdValid ? 'text-primary' : 'text-text-muted'}`} />
              <p className="text-sm font-medium text-text mb-1">
                {isDragging && isVehicleIdValid ? t('recording.drop_audio_here') : t('recording.drag_drop_audio')}
              </p>
              <p className="text-xs text-text-muted mb-3">
                MP3, WAV, M4A, OGG, WebM, AAC, FLAC
              </p>
              <label
                className={`btn-secondary inline-flex items-center gap-2 ${isVehicleIdValid ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                onClick={(e) => { if (!isVehicleIdValid) e.preventDefault(); }}
              >
                <Upload size={16} />
                <span>{t('common.browse_files')}</span>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleFileInput}
                  disabled={!isVehicleIdValid}
                  className="hidden"
                />
              </label>
            </div>
          );
        })()}
      </main>

      {/* Delete All Confirmation Dialog */}
      <Dialog
        open={showDeleteAllDialog}
        onClose={() => !isDeleting && setShowDeleteAllDialog(false)}
        title={t('recording.delete_confirm_title')}
        actions={
          <>
            <button
              onClick={() => setShowDeleteAllDialog(false)}
              disabled={isDeleting}
              className="btn-secondary"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleDeleteAll}
              disabled={isDeleting}
              className="btn-primary bg-error hover:bg-error-dark"
            >
              {isDeleting ? t('common.deleting') : t('common.delete')}
            </button>
          </>
        }
      >
        {t('recording.delete_confirm_message')}
      </Dialog>
    </div>
  );
};

export default VehicleInput;
